import { Message } from '../../types/chat';

/**
 * Helper functions for message creation and processing
 */

let messageCounter = 0; // Global counter for extra uniqueness

export function createUserMessage(content: string): Message {
  // Use high precision timestamp + counter + random component
  const timestamp = Date.now();
  const counter = ++messageCounter;
  const randomComponent = Math.random().toString(36).substring(2, 15);
  const uniqueId = `user_${timestamp}_${counter}_${randomComponent}`;
  
  return {
    id: uniqueId,
    content,
    sender: 'user',
    timestamp: new Date(timestamp)
  };
}

export function createBotMessage(content: string = '', type?: string): Message {
  // Use high precision timestamp + counter + random component
  const timestamp = Date.now();
  const counter = ++messageCounter;
  const randomComponent = Math.random().toString(36).substring(2, 15);
  const uniqueId = `bot_${timestamp}_${counter}_${randomComponent}`;
  
  return {
    id: uniqueId,
    content,
    sender: 'bot',
    timestamp: new Date(timestamp),
    ...(type && { type })
  };
}

export function generateRandomSuffix(): string {
  // Generate a more robust random suffix with timestamp
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `${timestamp}_${randomPart}`;
}

export interface TripDetails {
  destination: string;
  startDate: Date;
  endDate: Date;
  travelGroup: string;
  preferences: {
    activityTypes: string[];
  };
}

export interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface PreparedMessages {
  claudeMessages: ClaudeMessage[];
  recentMessages: Message[];
  hasLongGap: boolean;
}

export function prepareMessagesForClaude(
  messages: Message[], 
  currentContent?: string
): PreparedMessages {
  const now = new Date();
  const recentMessages = messages.filter(msg => {
    const hoursAgo = (now.getTime() - new Date(msg.timestamp).getTime()) / (1000 * 60 * 60);
    return hoursAgo <= 24;
  });

  // Check for long gap (example: more than 1 hour)
  let hasLongGap = false;
  if (recentMessages.length > 0) {
    const lastMessageTime = new Date(recentMessages[recentMessages.length - 1].timestamp).getTime();
    const hoursSinceLastMessage = (now.getTime() - lastMessageTime) / (1000 * 60 * 60);
    hasLongGap = hoursSinceLastMessage > 1;
  }

  // Filter out widget trigger messages and help messages
  const filteredMessages = recentMessages.filter(msg => {
    // Skip weather widget triggers
    if (msg.content.startsWith('Let me check the current weather') || 
        msg.content.startsWith('Let me check the weather')) {
      return false;
    }
    // Skip help messages
    if (msg.type === 'help') {
      return false;
    }
    // Skip empty messages
    if (!msg.content || msg.content.trim() === '') {
      return false;
    }
    return true;
  });

  const claudeMessages: ClaudeMessage[] = filteredMessages.map(msg => ({
    role: msg.sender === 'bot' ? 'assistant' : 'user',
    content: msg.content
  }));

  if (currentContent) {
    claudeMessages.push({ role: 'user', content: currentContent });
  }

  return { claudeMessages, recentMessages, hasLongGap };
}